package com.isaias.fyattravel.web.pages.explore

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.components.LazyImage
import com.isaias.fyattravel.shared.data.model.SafariPackage
import com.isaias.fyattravel.shared.data.model.SAFARI_PACKAGES

@Composable
fun PackagesSection() {
    Div({
        attr("id", "packages")
        style {
            padding(3.em, 1.5.em)
            property("margin", "0 auto")
            maxWidth(1200.px)
        }
    }) {
        EyebrowLabel("Uganda Safari Packages")
        H2({
            style {
                fontSize(1.8.em)
                fontWeight("800")
                margin(0.35.em, 0.em, 1.em, 0.em)
                color(Color("#141210"))
            }
        }) { Text("Choose your Uganda experience") }

        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(280px, 1fr))")
                gap(1.25.em)
            }
        }) {
            SAFARI_PACKAGES.forEach { pkg -> PackageCard(pkg) }
        }
    }
}

@Composable
private fun PackageCard(pkg: SafariPackage) {
    Div({
        style {
            property("display", "flex")
            flexDirection(FlexDirection.Column)
            borderRadius(1.1.em)
            overflow("hidden")
            backgroundColor(Color("#FFFFFF"))
            property("box-shadow", "0 6px 20px rgba(0,0,0,0.08)")
            if (pkg.signature) {
                property("grid-column", "span 2")
                border(2.px, LineStyle.Solid, Color("#AE7930"))
            }
        }
    }) {
        Div({
            style {
                position(Position.Relative)
                height(200.px)
            }
        }) {
            LazyImage(src = pkg.imagePath, alt = pkg.imageAlt) {
                width(100.percent)
                height(100.percent)
            }
            if (pkg.signature) {
                Span({
                    style {
                        position(Position.Absolute)
                        top(0.9.em); left(0.9.em)
                        backgroundColor(Color("#AE7930"))
                        color(Color("#FFFFFF"))
                        fontSize(0.7.em)
                        fontWeight("700")
                        letterSpacing(0.14.em)
                        property("text-transform", "uppercase")
                        padding(0.35.em, 0.7.em)
                        borderRadius(0.4.em)
                    }
                }) { Text("Signature") }
            }
        }
        Div({
            style {
                padding(1.25.em)
                property("display", "flex")
                flexDirection(FlexDirection.Column)
                gap(0.6.em)
                property("flex-grow", "1")
            }
        }) {
            Div({
                style {
                    fontSize(0.72.em)
                    fontWeight("600")
                    letterSpacing(0.1.em)
                    property("text-transform", "uppercase")
                    color(Color("#8B5E24"))
                }
            }) { Text(pkg.park) }
            H3({
                style {
                    fontSize(1.15.em)
                    fontWeight("800")
                    margin(0.px)
                    color(Color("#141210"))
                }
            }) { Text(pkg.name) }
            Div({
                style {
                    fontSize(0.85.em)
                    fontWeight("600")
                    color(Color("#333333"))
                }
            }) { Text(pkg.duration) }
            Ul({
                style {
                    margin(0.4.em, 0.em, 0.2.em, 0.em)
                    padding(0.px, 0.px, 0.px, 1.1.em)
                    property("display", "flex")
                    flexDirection(FlexDirection.Column)
                    gap(0.3.em)
                }
            }) {
                pkg.highlights.forEach { point ->
                    Li({ style { fontSize(0.88.em); color(Color("#444444")) } }) { Text(point) }
                }
            }
            A(
                href = waLink(pkg.waMessage),
                attrs = {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                    style {
                        property("margin-top", "auto")
                        property("display", "inline-flex")
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        backgroundColor(Color("#AE7930"))
                        color(Color("#FFFFFF"))
                        fontWeight("700")
                        fontSize(0.9.em)
                        padding(0.7.em, 1.em)
                        borderRadius(0.6.em)
                        textDecoration("none")
                        cursor("pointer")
                    }
                }
            ) { Text("Enquire on WhatsApp") }
        }
    }
}

@Composable
fun DubaiAddOnSection() {
    Div({
        style {
            padding(2.5.em, 1.5.em)
            property("margin", "0 auto")
            maxWidth(1200.px)
        }
    }) {
        EyebrowLabel("Optional Add-On")
        Div({
            classes("explore-dubai-card")
            style {
                marginTop(0.9.em)
                alignItems(AlignItems.Center)
                maxWidth(760.px)
                backgroundColor(Color("#F5EDE0"))
                borderRadius(1.1.em)
                overflow("hidden")
            }
        }) {
            Div({ style { height(180.px) } }) {
                LazyImage(
                    src = "images/explore/dubai/dubai-skyline.jpg",
                    alt = "Dubai skyline with the Burj Khalifa at dusk"
                ) {
                    width(100.percent)
                    height(100.percent)
                }
            }
            Div({
                style {
                    padding(1.25.em, 1.5.em, 1.25.em, 0.px)
                    property("display", "flex")
                    flexDirection(FlexDirection.Column)
                    gap(0.5.em)
                }
            }) {
                H3({
                    style {
                        fontSize(1.1.em)
                        fontWeight("800")
                        margin(0.px)
                        color(Color("#141210"))
                    }
                }) { Text("Dubai Stopover") }
                P({
                    style {
                        fontSize(0.88.em)
                        color(Color("#444444"))
                        margin(0.px)
                    }
                }) {
                    Text(
                        "Most travelers add this as a short stopover before or after their " +
                            "Uganda safari — not the main event. Burj Khalifa, a desert safari, " +
                            "and the Dubai Mall."
                    )
                }
                Div({
                    style {
                        fontSize(0.8.em)
                        fontWeight("700")
                        color(Color("#8B5E24"))
                    }
                }) { Text("2-3 Days") }
                A(
                    href = waLink("Hi! I'd like to ask about adding the Dubai stopover to my Uganda safari."),
                    attrs = {
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                        style {
                            property("display", "inline-flex")
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            property("width", "fit-content")
                            backgroundColor(Color.transparent)
                            color(Color("#8B5E24"))
                            border(1.5.px, LineStyle.Solid, Color("#8B5E24"))
                            fontWeight("700")
                            fontSize(0.85.em)
                            padding(0.55.em, 1.em)
                            borderRadius(0.6.em)
                            textDecoration("none")
                            cursor("pointer")
                        }
                    }
                ) { Text("Ask about this add-on") }
            }
        }
    }
}
